import { useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import Chip from '@mui/material/Chip';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { LocationDto, ZoneDto } from '@/types/organization';
import type { ConfirmProductionPutawayRequest, ProductionPutawayAllocationInput, ProductionPutawayTaskDto } from '@/types/production';
import type { ProductionPrintedLabelDto } from '@/types/productionLabeling';

// One row in the dialog's allocation table — productionPrintedLabelId set means this row is fixed
// to one physical barcode (qty locked to that label's own qty, no add/remove); null means a manual
// split row the operator can freely add, remove, and resize. `checked` decides whether this row is
// part of THIS confirm — unchecked rows are left for a later, partial-putaway pass (see
// ProductionPutawayService.ConfirmAsync's partial-confirm support).
interface AllocationRow {
  id: string;
  productionPrintedLabelId: string | null;
  barcodeValue?: string;
  qty: number;
  locationId: string;
  checked: boolean;
  // Only collected/validated when Process Configuration's "Barcode Required" is on.
  scannedBarcode?: string;
}

export default function ProductionPutawayPage() {
  const { t } = useTranslation('productionPutaway');
  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['production-putaway-tasks-pending'],
    queryFn: async () => (await apiClient.get<ProductionPutawayTaskDto[]>('/production-putaway-tasks/pending')).data,
  });

  const { data: config } = useProcessConfiguration();
  const barcodeMandatory = config?.barcodeMandatory ?? false;

  const [selectedTask, setSelectedTask] = useState<ProductionPutawayTaskDto | null>(null);
  const [rows, setRows] = useState<AllocationRow[]>([]);
  const [remarks, setRemarks] = useState('');
  const [bulkLocationId, setBulkLocationId] = useState('');
  // The one shared scan input for barcode-mode — replaces a per-row scan field. Typing/scanning a
  // value that exactly matches a not-yet-checked row's barcode auto-checks that row, records the
  // scanned value onto it, moves it to the top of the table, and clears this field for the next
  // scan.
  const [scanInput, setScanInput] = useState('');
  const [locationTypeFilter, setLocationTypeFilter] = useState<'All' | 'Zone' | 'Rack' | 'Bin'>('All');
  const [zoneFilterId, setZoneFilterId] = useState('');

  const { data: locations = [] } = useQuery({
    queryKey: ['locations', selectedTask?.warehouseId],
    queryFn: async () => (await apiClient.get<LocationDto[]>('/locations', { params: { warehouseId: selectedTask!.warehouseId } })).data,
    enabled: Boolean(selectedTask?.warehouseId),
  });
  const { data: zones = [] } = useQuery({
    queryKey: ['zones', selectedTask?.warehouseId],
    queryFn: async () => (await apiClient.get<ZoneDto[]>('/zones', { params: { warehouseId: selectedTask!.warehouseId } })).data,
    enabled: Boolean(selectedTask?.warehouseId),
  });
  const zoneOptions = zones.filter((z) => z.status === 'Active').map((z) => ({ value: z.id, label: z.name }));

  const warehouseLocations = locations.filter((l) => {
    if (l.status !== 'Active') return false;
    if (locationTypeFilter === 'Zone') return zoneFilterId ? l.zoneId === zoneFilterId : false;
    return locationTypeFilter === 'All' || l.locationType === locationTypeFilter;
  });
  const locationOptions = warehouseLocations.map((l) => ({
    value: l.id,
    label: `${l.zoneName ? `${l.zoneName} / ` : ''}${l.code} (${l.locationType})`,
  }));

  const { data: printedLabels = [] } = useQuery({
    queryKey: ['production-printed-labels', selectedTask?.productionReceiptLineId],
    queryFn: async () => (await apiClient.get<ProductionPrintedLabelDto[]>('/production-printed-labels', { params: { productionReceiptLineId: selectedTask!.productionReceiptLineId } })).data,
    enabled: Boolean(selectedTask?.hasPrintedLabels),
  });
  // Already-shelved labels (locationId set, from an earlier partial confirm) never offer
  // themselves again — the dialog only ever shows what's actually still remaining.
  const remainingPrintedLabels = printedLabels.filter((l) => !l.locationId);

  const remainingQty = selectedTask ? selectedTask.qty - selectedTask.allocatedQty : 0;

  const openTask = (task: ProductionPutawayTaskDto) => {
    setSelectedTask(task);
    setRemarks('');
    setBulkLocationId('');
    setLocationTypeFilter('All');
    setZoneFilterId('');
    setScanInput('');
    if (!task.hasPrintedLabels) {
      setRows([{ id: 'row-0', productionPrintedLabelId: null, qty: task.qty - task.allocatedQty, locationId: task.materialDefaultLocationId ?? '', checked: true }]);
    }
  };

  // Barcode-wise rows depend on printedLabels, which loads asynchronously after the dialog is
  // already open — separate from openTask so it doesn't race the initial state above.
  useEffect(() => {
    if (!selectedTask?.hasPrintedLabels) return;
    setRows(
      remainingPrintedLabels.map((label) => ({
        id: label.id,
        productionPrintedLabelId: label.id,
        barcodeValue: label.barcodeValue,
        qty: label.qty,
        locationId: selectedTask.materialDefaultLocationId ?? '',
        // Scan-driven selection when Barcode Required is on — nothing is pre-checked, each row
        // only checks itself once its own barcode is actually scanned (see handleScanInputChange).
        checked: !barcodeMandatory,
        scannedBarcode: '',
      })),
    );
    // barcodeMandatory deliberately omitted — re-running this on a background config refetch would
    // wipe out in-progress scan state while the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask?.id, printedLabels]);

  // "Barcode Required" (Process Configuration → Compliance & Workflow): mirrors PutawayPage's own
  // blockedNoBarcode/rowScanMatches logic for GRN putaway.
  const blockedNoBarcode = barcodeMandatory && Boolean(selectedTask) && !selectedTask?.hasPrintedLabels;

  const barcodeMode = Boolean(selectedTask?.hasPrintedLabels);
  const rowScanMatches = (row: AllocationRow) =>
    !barcodeMandatory || !barcodeMode || (row.scannedBarcode ?? '').trim().toUpperCase() === (row.barcodeValue ?? '').toUpperCase();

  // Only checked rows are part of this confirm — an unchecked row is left in place for a later,
  // partial pass instead of being submitted now. Partial confirms only make sense for barcode-wise
  // rows (each one is a specific physical unit to resume against later) — a manual quantity split
  // has no such identity, so it always requires every row and the full remaining quantity in one
  // go, mirroring ProductionPutawayService.ConfirmAsync's own server-side rule.
  const checkedRows = barcodeMode ? rows.filter((r) => r.checked) : rows;
  const totalOfRows = rows.reduce((sum, r) => sum + r.qty, 0);
  const totalChecked = checkedRows.reduce((sum, r) => sum + r.qty, 0);
  const allChecked = rows.length > 0 && checkedRows.length === rows.length;
  const isValid =
    !blockedNoBarcode &&
    checkedRows.length > 0 &&
    checkedRows.every((r) => r.locationId && r.qty > 0 && rowScanMatches(r)) &&
    Boolean(selectedTask) &&
    (barcodeMode ? totalChecked <= remainingQty : totalChecked === remainingQty);


  const confirmMutation = useMutation({
    mutationFn: async () => {
      const allocations: ProductionPutawayAllocationInput[] = checkedRows.map((r) => ({
        productionPrintedLabelId: r.productionPrintedLabelId,
        qty: r.qty,
        locationId: r.locationId,
        scannedBarcode: r.scannedBarcode || null,
      }));
      const payload: ConfirmProductionPutawayRequest = { allocations, remarks: remarks || null };
      const response = await apiClient.post<ProductionPutawayTaskDto>(`/production-putaway-tasks/${selectedTask!.id}/confirm`, payload);
      return response.data;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['production-putaway-tasks-pending'] });
      if (updated.status === 'Completed') {
        setSelectedTask(null);
        return;
      }
      const submittedIds = new Set(checkedRows.map((r) => r.id));
      setRows((prev) => prev.filter((r) => !submittedIds.has(r.id)));
      setSelectedTask(updated);
      queryClient.invalidateQueries({ queryKey: ['production-printed-labels', updated.productionReceiptLineId] });
    },
  });

  const columns: DataTableColumn<ProductionPutawayTaskDto>[] = [
    { field: 'productionReceiptNumber', headerName: t('table.receipt') },
    { field: 'materialName', headerName: t('table.material'), render: (r) => `${r.materialCode} — ${r.materialName}` },
    { field: 'batchNo', headerName: t('table.batch'), render: (r) => r.batchNo ?? '—' },
    {
      field: 'qty',
      headerName: t('table.qty'),
      render: (r) =>
        r.allocatedQty > 0 ? (
          <Box sx={{ minWidth: 120 }}>
            <LinearProgress variant="determinate" value={Math.min(100, (r.allocatedQty / r.qty) * 100)} sx={{ mb: 0.5 }} />
            <Typography variant="caption" color="text.secondary">
              {t('table.progress', { allocated: r.allocatedQty, total: r.qty })}
            </Typography>
          </Box>
        ) : (
          r.qty
        ),
    },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'suggestedLocationCode', headerName: t('table.suggested'), render: (r) => r.suggestedLocationCode ?? '—' },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'action', headerName: '', render: (r) => (
        <IfPermitted permission={PERMISSIONS.PRODUCTION_EXECUTE}>
          <Button size="small" variant="outlined" onClick={() => openTask(r)}>
            {t('confirm')}
          </Button>
        </IfPermitted>
      ),
    },
  ];

  const handleAddRow = () => {
    const unallocated = Math.max(0, remainingQty - totalOfRows);
    setRows((prev) => [
      ...prev,
      { id: `row-${prev.length}-${Date.now()}`, productionPrintedLabelId: null, qty: unallocated, locationId: selectedTask?.materialDefaultLocationId ?? '', checked: true },
    ]);
  };

  const handleRowQtyChange = (id: string, qty: number) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, qty } : r)));
  };

  const handleRowLocationChange = (id: string, locationId: string) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, locationId } : r)));
  };

  // Tries to match `value` against a not-yet-checked row's barcode — see PutawayPage's identical
  // helper for the full rationale (matches on every keystroke, plus an explicit Enter fallback).
  const tryMatchScan = (value: string): boolean => {
    const trimmed = value.trim().toUpperCase();
    const matchId = trimmed ? rows.find((r) => !r.checked && (r.barcodeValue ?? '').toUpperCase() === trimmed)?.id : undefined;
    if (!matchId) {
      return false;
    }
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === matchId);
      if (idx === -1) return prev;
      const updated = [...prev];
      const [row] = updated.splice(idx, 1);
      updated.unshift({ ...row, checked: true, scannedBarcode: value.trim() });
      return updated;
    });
    setScanInput('');
    return true;
  };

  const handleScanInputChange = (value: string) => {
    if (!tryMatchScan(value)) {
      setScanInput(value);
    }
  };

  const handleScanKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryMatchScan(scanInput);
    }
  };

  const handleBulkLocationChange = (locationId: string) => {
    setBulkLocationId(locationId);
    setRows((prev) => prev.map((r) => ({ ...r, locationId })));
  };

  const handleLocationTypeFilterChange = (value: 'All' | 'Zone' | 'Rack' | 'Bin' | null) => {
    if (!value) return;
    setLocationTypeFilter(value);
    if (value !== 'Zone') setZoneFilterId('');
    if (value === 'All') return;

    const staysValid = (locationId: string) => {
      const loc = locations.find((l) => l.id === locationId);
      if (!loc) return true;
      return value === 'Zone' ? loc.zoneId === zoneFilterId : loc.locationType === value;
    };
    setRows((prev) => prev.map((r) => (staysValid(r.locationId) ? r : { ...r, locationId: '' })));
    setBulkLocationId((prev) => (staysValid(prev) ? prev : ''));
  };

  const handleZoneFilterChange = (newZoneId: string) => {
    setZoneFilterId(newZoneId);
    const staysValid = (locationId: string) => {
      const loc = locations.find((l) => l.id === locationId);
      return !loc || loc.zoneId === newZoneId;
    };
    setRows((prev) => prev.map((r) => (staysValid(r.locationId) ? r : { ...r, locationId: '' })));
    setBulkLocationId((prev) => (staysValid(prev) ? prev : ''));
  };

  const handleRemoveRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />

      <DataTable columns={columns} rows={tasks} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="production-putaway-tasks" />

      <Dialog open={Boolean(selectedTask)} onClose={() => setSelectedTask(null)} maxWidth={barcodeMode ? 'md' : 'sm'} fullWidth>
        {selectedTask && (
          <>
            <DialogTitle>
              {t('dialog.title')}
              <Typography variant="body2" color="text.secondary">
                {t('dialog.subtitle', { receipt: selectedTask.productionReceiptNumber, qty: selectedTask.qty })}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {blockedNoBarcode ? (
                  <Alert severity="error">{t('dialog.noBarcodeBlocked')}</Alert>
                ) : (
                  <>
                <Alert severity="info">{barcodeMode ? t('dialog.barcodeMode') : t('dialog.splitMode')}</Alert>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {t('dialog.locationType')}
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    exclusive
                    size="small"
                    value={locationTypeFilter}
                    onChange={(_, value: 'All' | 'Zone' | 'Rack' | 'Bin' | null) => handleLocationTypeFilterChange(value)}
                  >
                    <ToggleButton value="All">{t('dialog.typeAll')}</ToggleButton>
                    <ToggleButton value="Zone">{t('dialog.typeZone')}</ToggleButton>
                    <ToggleButton value="Rack">{t('dialog.typeRack')}</ToggleButton>
                    <ToggleButton value="Bin">{t('dialog.typeBin')}</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>

                {locationTypeFilter === 'Zone' && (
                  <SearchableSelect label={t('dialog.zone')} value={zoneFilterId} onChange={handleZoneFilterChange} fullWidth options={zoneOptions} />
                )}

                {barcodeMode && (
                  <SearchableSelect
                    label={t('dialog.assignAllLocation')}
                    value={bulkLocationId}
                    onChange={handleBulkLocationChange}
                    fullWidth
                    options={locationOptions}
                    helperText={t('dialog.assignAllLocationHelper')}
                  />
                )}

                {barcodeMode && barcodeMandatory && (
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <TextField
                      label={t('dialog.scanBarcode')}
                      value={scanInput}
                      onChange={(e) => handleScanInputChange(e.target.value)}
                      onKeyDown={handleScanKeyDown}
                      helperText={t('dialog.scanBarcodeHelper')}
                      fullWidth
                      autoFocus
                    />
                    {/* Live scanned-vs-total counter — at-a-glance progress while working through a
                        multi-label putaway, so the operator doesn't have to count ticked rows. */}
                    <Chip
                      label={t('dialog.scannedOfTotal', { scanned: totalChecked, total: remainingQty })}
                      color={totalChecked >= remainingQty ? 'success' : 'warning'}
                      sx={{ mt: 1.25, fontWeight: 700, minWidth: 92, flexShrink: 0 }}
                    />
                  </Stack>
                )}

                {barcodeMode ? (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" />
                          <TableCell>{t('dialog.itemCode')}</TableCell>
                          <TableCell>{t('dialog.itemName')}</TableCell>
                          <TableCell align="right">{t('table.qty')}</TableCell>
                          <TableCell>{t('dialog.barcode')}</TableCell>
                          <TableCell sx={{ minWidth: 200 }}>{t('dialog.location')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => {
                          const scanMatches = rowScanMatches(row);
                          return (
                          <TableRow key={row.id} hover selected={row.checked}>
                            <TableCell padding="checkbox">
                              {/* Read-only — selection is scan-driven only (see the Scan Barcode field
                                  above); manually ticking a row here is deliberately not possible. */}
                              <Checkbox size="small" checked={row.checked} disabled />
                            </TableCell>
                            <TableCell>{selectedTask.materialCode}</TableCell>
                            <TableCell>{selectedTask.materialName}</TableCell>
                            <TableCell align="right">{row.qty}</TableCell>
                            <TableCell sx={row.checked && barcodeMandatory && !scanMatches ? { color: 'error.main' } : undefined}>
                              {row.barcodeValue}
                            </TableCell>
                            <TableCell sx={{ minWidth: 200 }}>
                              <SearchableSelect
                                label={t('dialog.location')}
                                value={row.locationId}
                                onChange={(value) => handleRowLocationChange(row.id, value)}
                                fullWidth
                                options={locationOptions}
                              />
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <Stack spacing={1.5}>
                    {rows.map((row, i) => (
                      <Stack key={row.id} direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                          size="small"
                          type="number"
                          label={t('dialog.rowN', { n: i + 1 })}
                          fullWidth
                          value={row.qty}
                          onChange={(e) => handleRowQtyChange(row.id, Number(e.target.value))}
                        />
                        <SearchableSelect
                          label={t('dialog.location')}
                          value={row.locationId}
                          onChange={(value) => handleRowLocationChange(row.id, value)}
                          fullWidth
                          options={locationOptions}
                        />
                        <IconButton size="small" disabled={rows.length <= 1} onClick={() => handleRemoveRow(row.id)} sx={{ mt: 0.5 }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ))}
                  </Stack>
                )}

                {!barcodeMode && (
                  <Button size="small" startIcon={<AddIcon />} onClick={handleAddRow} sx={{ alignSelf: 'flex-start' }}>
                    {t('dialog.addRow')}
                  </Button>
                )}

                <Typography
                  variant="caption"
                  color={barcodeMode ? (totalChecked > remainingQty ? 'error' : 'text.secondary') : (totalChecked === remainingQty ? 'text.secondary' : 'error')}
                >
                  {t('dialog.allocatedOf', { allocated: totalChecked, total: remainingQty })}
                  {barcodeMode
                    ? totalChecked > remainingQty && ` — ${t('dialog.mismatchHint', { total: remainingQty })}`
                    : totalChecked !== remainingQty && ` — ${t('dialog.exactMismatchHint', { total: remainingQty })}`}
                </Typography>
                {selectedTask.allocatedQty > 0 && (
                  <Alert severity="info" icon={false}>
                    {t('dialog.alreadyPutAway', { allocated: selectedTask.allocatedQty, total: selectedTask.qty })}
                  </Alert>
                )}

                <TextField label={t('dialog.remarks')} multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth />
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setSelectedTask(null)}>{t('cancel')}</Button>
              <Button variant="contained" disabled={!isValid || confirmMutation.isPending} onClick={() => confirmMutation.mutate()}>
                {allChecked ? t('confirm') : t('dialog.confirmPartial', { count: checkedRows.length })}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
